import type {
  ApplicationState,
  Bounds,
  Command,
  ContainerKind,
  Matrix2D,
  ObjectId,
  Size,
  SpatialContainer,
  SpatialObject,
  Transform,
  Vec2,
  WorkspaceDocument,
} from '@/types';
import { asObjectId, isContainer } from '@/types';
import { createCommand } from '@/core/commands/createCommand';
import { IDENTITY_MATRIX, applyMatrix, invert, normalizeAngle } from '@/core/geometry/transform';
import { objectType } from '@/core/scene/objectTypes';
import { boxCentre } from '@/core/interaction/manipulation';
import { createId } from '@/utils/id';

type Doc = WorkspaceDocument;

/** Resolves an object's local→world matrix; supplied by the scene graph at dispatch time. */
export type FrameResolver = (id: ObjectId) => Matrix2D | undefined;

const touch = <T extends SpatialObject>(obj: T, now: number): T => ({
  ...obj,
  metadata: { ...obj.metadata, updatedAt: now },
});

/** Returns a copy of `record` without the given keys (no mutation, no `delete`). */
const omitKeys = <K extends string, V>(record: Readonly<Record<K, V>>, keys: ReadonlySet<K>): Record<K, V> =>
  Object.fromEntries((Object.entries(record) as [K, V][]).filter(([k]) => !keys.has(k))) as Record<K, V>;

/** Maps `fn` over the given objects; returns `doc` unchanged when nothing changed. */
const mapObjects = (doc: Doc, ids: readonly ObjectId[], fn: (o: SpatialObject) => SpatialObject): Doc => {
  let objects: Record<ObjectId, SpatialObject> | null = null;
  for (const id of ids) {
    const o = doc.objects[id];
    if (!o) continue;
    const next = fn(o);
    if (next === o) continue;
    objects ??= { ...doc.objects };
    objects[id] = next;
  }
  return objects ? { ...doc, objects } : doc;
};

/**
 * Drops ids whose ancestor is also in the set. Moving or duplicating a parent already
 * carries its children, so acting on both would apply the change twice.
 */
export const topLevelIds = (doc: Doc, ids: readonly ObjectId[]): ObjectId[] => {
  const set = new Set(ids);
  return ids.filter((id) => {
    if (!doc.objects[id]) return false;
    const guard = new Set<ObjectId>();
    let parent = doc.objects[id]?.parentId ?? null;
    while (parent !== null && !guard.has(parent)) {
      if (set.has(parent)) return false;
      guard.add(parent);
      parent = doc.objects[parent]?.parentId ?? null;
    }
    return true;
  });
};

const toRecord = (list: readonly SpatialObject[]): Record<ObjectId, SpatialObject> =>
  Object.fromEntries(list.map((o) => [o.id, o]));

/**
 * Generic snapshot command: `after` replaces `before`, `removed` are deleted on
 * execute and restored on undo. Most structural edits reduce to this, which keeps
 * undo exact regardless of how complex the forward computation was.
 */
const snapshotCommand = (
  name: string,
  label: string,
  before: readonly SpatialObject[],
  after: readonly SpatialObject[],
  removed: readonly SpatialObject[] = [],
): Command<Doc> => {
  const removedIds = new Set(removed.map((o) => o.id));
  const afterIds = new Set(after.map((o) => o.id));
  const beforeOnly = before.filter((o) => !afterIds.has(o.id)); // objects that existed only before (e.g. dissolved group)
  return createCommand<Doc>({
    name,
    label,
    execute: (d) => ({
      ...d,
      objects: { ...omitKeys(d.objects, new Set([...removedIds, ...beforeOnly.map((o) => o.id)])), ...toRecord(after) },
    }),
    undo: (d) => ({
      ...d,
      objects: {
        ...omitKeys(d.objects, new Set(after.filter((o) => !before.some((b) => b.id === o.id)).map((o) => o.id))),
        ...toRecord(before),
        ...toRecord(removed),
      },
    }),
  });
};

// ---------------------------------------------------------------------------
// Creation / deletion
// ---------------------------------------------------------------------------

export const addObjectsCommand = (objects: readonly SpatialObject[]): Command<Doc> =>
  createCommand<Doc>({
    name: 'object.add',
    label: objects.length === 1 ? `Add ${objects[0]?.kind ?? 'object'}` : `Add ${objects.length} objects`,
    execute: (doc) => ({ ...doc, objects: { ...doc.objects, ...toRecord(objects) } }),
    undo: (doc) => ({ ...doc, objects: omitKeys(doc.objects, new Set(objects.map((o) => o.id))) }),
  });

/** Every id in `ids` plus all their descendants, deduplicated, in the document. */
export const collectSubtrees = (doc: Doc, ids: readonly ObjectId[]): ObjectId[] => {
  const byParent = new Map<ObjectId, ObjectId[]>();
  for (const obj of Object.values(doc.objects)) {
    if (obj.parentId !== null) byParent.set(obj.parentId, [...(byParent.get(obj.parentId) ?? []), obj.id]);
  }
  const out: ObjectId[] = [];
  const seen = new Set<ObjectId>();
  const stack = [...ids];
  while (stack.length) {
    const id = stack.pop();
    if (id === undefined || seen.has(id) || !doc.objects[id]) continue;
    seen.add(id);
    out.push(id);
    stack.push(...(byParent.get(id) ?? []));
  }
  return out;
};

/** Deletes objects and their descendants; connections touching them go too; parents' childIds are trimmed. */
export const deleteObjectsCommand = (doc: Doc, ids: readonly ObjectId[], now = Date.now()): Command<Doc> => {
  const allIds = collectSubtrees(doc, ids);
  const removed = allIds.map((id) => doc.objects[id]).filter((o): o is SpatialObject => o !== undefined);
  const removedIds = new Set(allIds);
  const removedConnections = Object.values(doc.connections).filter(
    (c) => removedIds.has(c.sourceId) || removedIds.has(c.targetId),
  );
  const parents = new Set(
    removed.map((o) => o.parentId).filter((p): p is ObjectId => p !== null && !removedIds.has(p)),
  );
  return createCommand<Doc>({
    name: 'object.delete',
    label: removed.length === 1 ? 'Delete object' : `Delete ${removed.length} objects`,
    execute: (d) => {
      const trimmed = mapObjects({ ...d, objects: omitKeys(d.objects, removedIds) }, [...parents], (p) =>
        isContainer(p) ? touch({ ...p, childIds: p.childIds.filter((c) => !removedIds.has(c)) }, now) : p,
      );
      return { ...trimmed, connections: omitKeys(d.connections, new Set(removedConnections.map((c) => c.id))) };
    },
    undo: (d) => {
      const restored = { ...d, objects: { ...d.objects, ...toRecord(removed) } };
      const relinked = mapObjects(restored, [...parents], (p) => {
        if (!isContainer(p)) return p;
        const missing = removed.filter((o) => o.parentId === p.id && !p.childIds.includes(o.id)).map((o) => o.id);
        return touch({ ...p, childIds: [...p.childIds, ...missing] }, now);
      });
      return {
        ...relinked,
        connections: { ...d.connections, ...Object.fromEntries(removedConnections.map((c) => [c.id, c])) },
      };
    },
  });
};

/**
 * Deep-duplicates objects (subtrees included) with fresh ids, offset in their parent
 * frame. Roots of the duplicated set keep their parent; internal parent links are
 * remapped to the new ids. Connections between duplicated objects are not copied.
 */
export const duplicateObjectsCommand = (
  doc: Doc,
  ids: readonly ObjectId[],
  offset: Vec2 = { x: 24, y: 24 },
  now = Date.now(),
  makeId: () => ObjectId = () => asObjectId(createId('obj')),
): { readonly command: Command<Doc>; readonly newIds: readonly ObjectId[] } | null => {
  const allIds = collectSubtrees(doc, ids);
  if (allIds.length === 0) return null;
  const idMap = new Map<ObjectId, ObjectId>(allIds.map((id) => [id, makeId()]));
  const mapped = (id: ObjectId): ObjectId => idMap.get(id) ?? id;
  const topLevel = new Set(topLevelIds(doc, ids));
  const copies: SpatialObject[] = [];
  for (const src of allIds.map((id) => doc.objects[id]).filter((o): o is SpatialObject => o !== undefined)) {
    const id = src.id;
    const isRootOfCopy = topLevel.has(id) || (src.parentId !== null && !idMap.has(src.parentId));
    const parentId = src.parentId !== null && idMap.has(src.parentId) ? mapped(src.parentId) : src.parentId;
    const shifted: Transform = isRootOfCopy
      ? {
          ...src.transform,
          position: { x: src.transform.position.x + offset.x, y: src.transform.position.y + offset.y },
        }
      : src.transform;
    const copyBase = {
      ...src,
      id: mapped(id),
      parentId,
      transform: shifted,
      metadata: { ...src.metadata, createdAt: now, updatedAt: now },
    };
    copies.push(
      isContainer(copyBase)
        ? { ...copyBase, childIds: copyBase.childIds.filter((c) => idMap.has(c) || doc.objects[c]).map(mapped) }
        : copyBase,
    );
  }
  // External parents (unchanged containers) gain the copied roots as children.
  const externalParents = new Map<ObjectId, ObjectId[]>();
  for (const c of copies) {
    if (c.parentId !== null && !idMap.has(c.parentId) && doc.objects[c.parentId]) {
      externalParents.set(c.parentId, [...(externalParents.get(c.parentId) ?? []), c.id]);
    }
  }
  const maxZ = Math.max(0, ...Object.values(doc.objects).map((o) => o.zIndex));
  const lifted = copies.map((c, i) => ({ ...c, zIndex: maxZ + 1 + i }));
  const newIds = lifted.map((c) => c.id);
  const parentBefore = [...externalParents.keys()].map((p) => doc.objects[p]).filter((o): o is SpatialObject => !!o);
  const parentAfter = parentBefore.map((p) =>
    isContainer(p) ? touch({ ...p, childIds: [...p.childIds, ...(externalParents.get(p.id) ?? [])] }, now) : p,
  );
  const command = snapshotCommand(
    'object.duplicate',
    lifted.length === 1 ? 'Duplicate object' : `Duplicate ${lifted.length} objects`,
    parentBefore,
    [...parentAfter, ...lifted],
  );
  return { command, newIds };
};

// ---------------------------------------------------------------------------
// Transform edits (mergeable during drags)
// ---------------------------------------------------------------------------

interface MoveCommand extends Command<Doc> {
  readonly moveKey: string;
  readonly moveDelta: Vec2;
}
const isMove = (c: Command<Doc>): c is MoveCommand => c.name === 'object.move' && 'moveKey' in c;

/**
 * Moves objects by a delta in their parent frame. Consecutive moves of the same id
 * set merge into a single history entry, so a drag is one undo step. Locked objects
 * are skipped.
 */
export const moveObjectsCommand = (ids: readonly ObjectId[], delta: Vec2, now = Date.now()): Command<Doc> => {
  const key = [...ids].sort().join('|');
  const shift = (d: Doc, dx: number, dy: number): Doc =>
    mapObjects(d, topLevelIds(d, ids), (o) =>
      o.locked
        ? o
        : touch(
            {
              ...o,
              transform: {
                ...o.transform,
                position: { x: o.transform.position.x + dx, y: o.transform.position.y + dy },
              },
            },
            now,
          ),
    );
  const command: Command<Doc> = createCommand<Doc>({
    name: 'object.move',
    label: ids.length === 1 ? 'Move object' : `Move ${ids.length} objects`,
    execute: (d) => shift(d, delta.x, delta.y),
    undo: (d) => shift(d, -delta.x, -delta.y),
    merge: (next) => {
      if (isMove(next) && next.moveKey === key) {
        return moveObjectsCommand(ids, { x: delta.x + next.moveDelta.x, y: delta.y + next.moveDelta.y }, now);
      }
      // A drag ends with "bring to front"; fold it in so the whole gesture is one undo step.
      if (next.name === 'object.bring-to-front') {
        return createCommand<Doc>({
          name: 'object.move',
          label: command.label,
          execute: (d) => next.execute(shift(d, delta.x, delta.y)),
          undo: (d) => shift(next.undo(d), -delta.x, -delta.y),
        });
      }
      return null;
    },
  });
  return Object.assign(command, { moveKey: key, moveDelta: delta });
};

export interface TransformPatch {
  readonly position?: Vec2;
  readonly rotation?: number;
  readonly scale?: Vec2;
  readonly size?: Size;
}

interface TransformCommand extends Command<Doc> {
  readonly transformId: ObjectId;
  readonly transformAfter: { readonly transform: Transform; readonly size: Size };
}
const isTransformCommand = (c: Command<Doc>): c is TransformCommand =>
  c.name === 'object.transform' && 'transformId' in c;

const makeTransformCommand = (
  id: ObjectId,
  label: string,
  before: { readonly transform: Transform; readonly size: Size },
  after: { readonly transform: Transform; readonly size: Size },
  now: number,
): TransformCommand => {
  const apply = (state: typeof after) => (d: Doc) =>
    mapObjects(d, [id], (o) => touch({ ...o, transform: state.transform, size: state.size }, now));
  const command = createCommand<Doc>({
    name: 'object.transform',
    label,
    execute: apply(after),
    undo: apply(before),
    // A resize/rotate drag emits many patches; they collapse into one entry whose undo is the pre-drag state.
    merge: (next) =>
      isTransformCommand(next) && next.transformId === id
        ? makeTransformCommand(id, label, before, next.transformAfter, now)
        : null,
  });
  return Object.assign(command, { transformId: id, transformAfter: after });
};

/**
 * Applies a transform/size patch to one object. Size is clamped to the type's
 * minimum, scale to a sane range, rotation normalised. Null for unknown or locked objects.
 */
export const setTransformCommand = (
  doc: Doc,
  id: ObjectId,
  patch: TransformPatch,
  label = 'Transform object',
  now = Date.now(),
): Command<Doc> | null => {
  const o = doc.objects[id];
  if (!o || o.locked) return null;
  const def = objectType(o.kind);
  const size: Size = patch.size
    ? {
        width: Math.max(def.minSize.width, patch.size.width),
        height: Math.max(def.minSize.height, patch.size.height),
      }
    : o.size;
  const clampScale = (v: number) => Math.min(16, Math.max(0.05, Math.abs(v) || 1));
  const transform: Transform = {
    position: patch.position ?? o.transform.position,
    rotation: patch.rotation !== undefined && def.rotatable ? normalizeAngle(patch.rotation) : o.transform.rotation,
    scale: patch.scale ? { x: clampScale(patch.scale.x), y: clampScale(patch.scale.y) } : o.transform.scale,
  };
  const same =
    size.width === o.size.width &&
    size.height === o.size.height &&
    transform.position.x === o.transform.position.x &&
    transform.position.y === o.transform.position.y &&
    transform.rotation === o.transform.rotation &&
    transform.scale.x === o.transform.scale.x &&
    transform.scale.y === o.transform.scale.y;
  if (same) return null;
  return makeTransformCommand(id, label, { transform: o.transform, size: o.size }, { transform, size }, now);
};

export const resizeObjectCommand = (doc: Doc, id: ObjectId, size: Size, position?: Vec2): Command<Doc> | null =>
  setTransformCommand(doc, id, position ? { size, position } : { size }, 'Resize object');

export const rotateObjectCommand = (doc: Doc, id: ObjectId, rotation: number): Command<Doc> | null =>
  setTransformCommand(doc, id, { rotation }, 'Rotate object');

// ---------------------------------------------------------------------------
// Properties
// ---------------------------------------------------------------------------

const propertyCommand = <K extends 'name' | 'zIndex' | 'visible' | 'locked'>(
  doc: Doc,
  ids: readonly ObjectId[],
  key: K,
  value: SpatialObject[K],
  name: string,
  label: string,
  now: number,
): Command<Doc> | null => {
  const before = ids.map((id) => doc.objects[id]).filter((o): o is SpatialObject => !!o && o[key] !== value);
  if (!before.length) return null;
  const after = before.map((o) => touch({ ...o, [key]: value }, now));
  return snapshotCommand(name, label, before, after);
};

export const renameObjectCommand = (doc: Doc, id: ObjectId, name: string, now = Date.now()) =>
  propertyCommand(
    doc,
    [id],
    'name',
    name.trim() || (doc.objects[id]?.name ?? 'Object'),
    'object.rename',
    'Rename object',
    now,
  );

export const setVisibilityCommand = (doc: Doc, ids: readonly ObjectId[], visible: boolean, now = Date.now()) =>
  propertyCommand(doc, ids, 'visible', visible, 'object.visibility', visible ? 'Show objects' : 'Hide objects', now);

export const setLockedCommand = (doc: Doc, ids: readonly ObjectId[], locked: boolean, now = Date.now()) =>
  propertyCommand(doc, ids, 'locked', locked, 'object.lock', locked ? 'Lock objects' : 'Unlock objects', now);

export const setZIndexCommand = (doc: Doc, id: ObjectId, zIndex: number, now = Date.now()) =>
  Number.isFinite(zIndex)
    ? propertyCommand(doc, [id], 'zIndex', Math.round(zIndex), 'object.z-index', 'Set z-index', now)
    : null;

export const bringToFrontCommand = (doc: Doc, ids: readonly ObjectId[], now = Date.now()): Command<Doc> => {
  const maxZ = Math.max(0, ...Object.values(doc.objects).map((o) => o.zIndex));
  const previous = new Map(ids.map((id) => [id, doc.objects[id]?.zIndex ?? 0]));
  return createCommand<Doc>({
    name: 'object.bring-to-front',
    label: 'Bring to front',
    execute: (d) => mapObjects(d, ids, (o) => touch({ ...o, zIndex: maxZ + 1 + ids.indexOf(o.id) }, now)),
    undo: (d) => mapObjects(d, ids, (o) => touch({ ...o, zIndex: previous.get(o.id) ?? o.zIndex }, now)),
  });
};

export const sendToBackCommand = (doc: Doc, ids: readonly ObjectId[], now = Date.now()): Command<Doc> => {
  const minZ = Math.min(0, ...Object.values(doc.objects).map((o) => o.zIndex));
  const previous = new Map(ids.map((id) => [id, doc.objects[id]?.zIndex ?? 0]));
  return createCommand<Doc>({
    name: 'object.send-to-back',
    label: 'Send to back',
    execute: (d) => mapObjects(d, ids, (o) => touch({ ...o, zIndex: minZ - ids.length + ids.indexOf(o.id) }, now)),
    undo: (d) => mapObjects(d, ids, (o) => touch({ ...o, zIndex: previous.get(o.id) ?? o.zIndex }, now)),
  });
};

/** Highest zIndex in the document, so new objects land on top. */
export const nextZIndex = (doc: Doc): number =>
  Object.values(doc.objects).reduce((max, o) => Math.max(max, o.zIndex), 0) + 1;

// ---------------------------------------------------------------------------
// Hierarchy
// ---------------------------------------------------------------------------

/** Local position in `parentId`'s frame that keeps the object's centre at the same world point. */
const relocate = (obj: SpatialObject, parentId: ObjectId | null, frames: FrameResolver): Transform => {
  const oldParent = obj.parentId === null ? IDENTITY_MATRIX : (frames(obj.parentId) ?? IDENTITY_MATRIX);
  const newParent = parentId === null ? IDENTITY_MATRIX : (frames(parentId) ?? IDENTITY_MATRIX);
  const inv = invert(newParent) ?? IDENTITY_MATRIX;
  const centreWorld = applyMatrix(oldParent, boxCentre(obj.transform, obj.size));
  const centreLocal = applyMatrix(inv, centreWorld);
  return {
    ...obj.transform,
    position: { x: centreLocal.x - obj.size.width / 2, y: centreLocal.y - obj.size.height / 2 },
  };
};

/**
 * Moves objects under a new parent (or to the root with `null`) while keeping their
 * world position. Rejects targets that are not containers or would create a cycle.
 */
export const reparentObjectsCommand = (
  doc: Doc,
  ids: readonly ObjectId[],
  parentId: ObjectId | null,
  frames: FrameResolver,
  now = Date.now(),
): Command<Doc> | null => {
  const parent = parentId === null ? null : doc.objects[parentId];
  if (parentId !== null && (!parent || !isContainer(parent))) return null;
  const moving = ids.map((id) => doc.objects[id]).filter((o): o is SpatialObject => !!o && o.parentId !== parentId);
  if (!moving.length) return null;
  // Cycle guard: the target must not be inside any moving subtree.
  const subtrees = new Set(
    collectSubtrees(
      doc,
      moving.map((o) => o.id),
    ),
  );
  if (parentId !== null && subtrees.has(parentId)) return null;

  const movingIds = new Set(moving.map((o) => o.id));
  const affectedParents = new Set<ObjectId>();
  for (const o of moving) if (o.parentId !== null) affectedParents.add(o.parentId);
  if (parentId !== null) affectedParents.add(parentId);

  const before: SpatialObject[] = [
    ...moving,
    ...[...affectedParents].map((p) => doc.objects[p]).filter((o): o is SpatialObject => !!o),
  ];
  const after: SpatialObject[] = [
    ...moving.map((o) => touch({ ...o, parentId, transform: relocate(o, parentId, frames) }, now)),
    ...[...affectedParents]
      .map((p) => doc.objects[p])
      .filter((o): o is SpatialContainer => !!o && isContainer(o))
      .map((p) => {
        const kept = p.childIds.filter((c) => !movingIds.has(c));
        const added = p.id === parentId ? moving.map((o) => o.id).filter((id) => !kept.includes(id)) : [];
        return touch({ ...p, childIds: [...kept, ...added] }, now);
      }),
  ];
  return snapshotCommand('object.reparent', parentId === null ? 'Move to root' : 'Move into container', before, after);
};

/**
 * Wraps objects in a new container sized to their union bounds. All objects must share
 * a parent (mixed parents are lifted to that common parent first by the caller).
 */
export const groupObjectsCommand = (
  doc: Doc,
  ids: readonly ObjectId[],
  kind: ContainerKind,
  worldBounds: Bounds,
  frames: FrameResolver,
  make: (input: {
    position: Vec2;
    size: Size;
    childIds: readonly ObjectId[];
    zIndex: number;
    parentId: ObjectId | null;
  }) => SpatialContainer,
  now = Date.now(),
): { readonly command: Command<Doc>; readonly containerId: ObjectId } | null => {
  const members = ids.map((id) => doc.objects[id]).filter((o): o is SpatialObject => !!o);
  if (members.length < 1) return null;
  const parentId = members[0]?.parentId ?? null;
  if (!members.every((m) => m.parentId === parentId)) return null;
  const parentFrame = parentId === null ? IDENTITY_MATRIX : (frames(parentId) ?? IDENTITY_MATRIX);
  const inv = invert(parentFrame) ?? IDENTITY_MATRIX;
  const pad = 16;
  const labelHeight = 28; // room for the container's label strip above the children
  const tl = applyMatrix(inv, { x: worldBounds.minX - pad, y: worldBounds.minY - pad - labelHeight });
  const size: Size = {
    width: worldBounds.maxX - worldBounds.minX + pad * 2,
    height: worldBounds.maxY - worldBounds.minY + pad * 2 + labelHeight,
  };
  // Containers sit behind their members so clicks on children reach the children.
  const zIndex = Math.min(...members.map((m) => m.zIndex)) - 1;
  const memberIds = members.map((m) => m.id);
  const container = make({ position: tl, size, childIds: memberIds, zIndex, parentId });

  // Children become relative to the container's top-left (containers are unrotated, unscaled).
  const children = members.map((m) =>
    touch(
      {
        ...m,
        parentId: container.id,
        transform: { ...m.transform, position: { x: m.transform.position.x - tl.x, y: m.transform.position.y - tl.y } },
      },
      now,
    ),
  );
  const parentBefore = parentId === null ? [] : [doc.objects[parentId]].filter((o): o is SpatialObject => !!o);
  const parentAfter = parentBefore.map((p) =>
    isContainer(p)
      ? touch({ ...p, childIds: [...p.childIds.filter((c) => !memberIds.includes(c)), container.id] }, now)
      : p,
  );
  const command = snapshotCommand(
    kind === 'group' ? 'object.group' : 'object.folder',
    kind === 'group' ? 'Group objects' : 'Put in folder',
    [...members, ...parentBefore],
    [container, ...children, ...parentAfter],
  );
  return { command, containerId: container.id };
};

/** Dissolves a container, lifting its children to the container's parent at the same world position. */
export const ungroupCommand = (
  doc: Doc,
  containerId: ObjectId,
  frames: FrameResolver,
  now = Date.now(),
): Command<Doc> | null => {
  const container = doc.objects[containerId];
  if (!container || !isContainer(container)) return null;
  const children = Object.values(doc.objects).filter((o) => o.parentId === containerId);
  const lifted = children.map((c) =>
    touch({ ...c, parentId: container.parentId, transform: relocate(c, container.parentId, frames) }, now),
  );
  const parentBefore =
    container.parentId === null ? [] : [doc.objects[container.parentId]].filter((o): o is SpatialObject => !!o);
  const parentAfter = parentBefore.map((p) =>
    isContainer(p)
      ? touch({ ...p, childIds: [...p.childIds.filter((c) => c !== containerId), ...lifted.map((c) => c.id)] }, now)
      : p,
  );
  return snapshotCommand(
    'object.ungroup',
    'Ungroup',
    [container, ...children, ...parentBefore],
    [...lifted, ...parentAfter],
  );
};

/** Collapses a container to its header strip (children become hidden and inert) or expands it back. */
export const setCollapsedCommand = (
  doc: Doc,
  id: ObjectId,
  collapsed: boolean,
  now = Date.now(),
): Command<Doc> | null => {
  const before = doc.objects[id];
  if (!before || !isContainer(before) || before.collapsed === collapsed) return null;
  const after: SpatialObject = collapsed
    ? touch(
        {
          ...before,
          collapsed: true,
          expandedSize: before.size,
          size: { width: before.size.width, height: COLLAPSED_HEIGHT },
        },
        now,
      )
    : touch({ ...before, collapsed: false, expandedSize: null, size: before.expandedSize ?? before.size }, now);
  return snapshotCommand('container.collapse', collapsed ? 'Collapse' : 'Expand', [before], [after]);
};

/** Header-only height of a collapsed container, in world units. */
export const COLLAPSED_HEIGHT = 28;

// ---------------------------------------------------------------------------
// Kind-specific content
// ---------------------------------------------------------------------------

interface NoteCommand extends Command<Doc> {
  readonly noteId: ObjectId;
  readonly noteContent: string;
}
const isNoteCommand = (c: Command<Doc>): c is NoteCommand => c.name === 'note.update-content' && 'noteId' in c;

const makeNoteCommand = (id: ObjectId, previous: string, content: string, now: number): NoteCommand => {
  const command = createCommand<Doc>({
    name: 'note.update-content',
    label: 'Edit note',
    execute: (d) => mapObjects(d, [id], (o) => (o.kind === 'note' ? touch({ ...o, content }, now) : o)),
    undo: (d) => mapObjects(d, [id], (o) => (o.kind === 'note' ? touch({ ...o, content: previous }, now) : o)),
    // Typing merges into one entry; undo restores the content from before typing began.
    merge: (next) =>
      isNoteCommand(next) && next.noteId === id ? makeNoteCommand(id, previous, next.noteContent, now) : null,
  });
  return Object.assign(command, { noteId: id, noteContent: content });
};

export const updateNoteContentCommand = (
  doc: Doc,
  id: ObjectId,
  content: string,
  now = Date.now(),
): Command<Doc> | null => {
  const before = doc.objects[id];
  if (before?.kind !== 'note') return null;
  return makeNoteCommand(id, before.content, content, now);
};

interface WindowStateCommand extends Command<Doc> {
  readonly windowId: ObjectId;
  readonly windowNext: ApplicationState;
}
const isWindowStateCommand = (c: Command<Doc>): c is WindowStateCommand =>
  c.name === 'window.set-state' && 'windowId' in c;

const makeWindowStateCommand = (
  id: ObjectId,
  previous: ApplicationState,
  next: ApplicationState,
  now: number,
): WindowStateCommand => {
  const command = createCommand<Doc>({
    name: 'window.set-state',
    label: 'Edit window',
    execute: (d) =>
      mapObjects(d, [id], (o) => (o.kind === 'window' ? touch({ ...o, applicationState: next }, now) : o)),
    undo: (d) =>
      mapObjects(d, [id], (o) => (o.kind === 'window' ? touch({ ...o, applicationState: previous }, now) : o)),
    merge: (n) =>
      isWindowStateCommand(n) && n.windowId === id ? makeWindowStateCommand(id, previous, n.windowNext, now) : null,
  });
  return Object.assign(command, { windowId: id, windowNext: next });
};

export const setApplicationStateCommand = (
  doc: Doc,
  id: ObjectId,
  next: ApplicationState,
  now = Date.now(),
): Command<Doc> | null => {
  const before = doc.objects[id];
  if (before?.kind !== 'window') return null;
  return makeWindowStateCommand(id, before.applicationState, next, now);
};
